import { useState, useRef, useEffect, KeyboardEvent } from "react";
import { X, Users, Plus, Search, User } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/providers/AuthProvider";
import type { EmailRecipient } from "@/types/email";

interface CandidateResult {
	id: string;
	name: string;
	email: string;
}

interface ListResult {
	id: string;
	name: string;
	candidate_count: number;
}

interface RecipientChipsProps {
	recipients: EmailRecipient[];
	listName?: string;
	onRemove?: (index: number) => void;
	onAdd?: (recipient: EmailRecipient) => void;
	onAddMultiple?: (recipients: EmailRecipient[]) => void;
	maxDisplay?: number;
}

export const RecipientChips = ({ recipients, listName, onRemove, onAdd, onAddMultiple, maxDisplay = 10 }: RecipientChipsProps) => {
	const { t } = useTranslation();
	const { currentOrganization } = useAuth();
	const [inputValue, setInputValue] = useState("");
	const [candidateResults, setCandidateResults] = useState<CandidateResult[]>([]);
	const [listResults, setListResults] = useState<ListResult[]>([]);
	const [showDropdown, setShowDropdown] = useState(false);
	const [isSearching, setIsSearching] = useState(false);
	const [loadingListId, setLoadingListId] = useState<string | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

	// Search candidates and lists as user types
	useEffect(() => {
		if (searchTimeoutRef.current)
			clearTimeout(searchTimeoutRef.current);

		const query = inputValue.trim();
		if (query.length < 2 || !currentOrganization?.id) {
			setCandidateResults([]);
			setListResults([]);
			setShowDropdown(false);
			return;
		}

		searchTimeoutRef.current = setTimeout(async () => {
			setIsSearching(true);
			try {
				const [candidatesRes, listsRes] = await Promise.all([
					supabase
						.from('candidates')
						.select('id, name, email')
						.eq('organization_uuid', currentOrganization.id)
						.not('email', 'is', null)
						.or(`name.ilike.%${query}%,email.ilike.%${query}%`)
						.limit(6),
					supabase
						.from('lists_with_candidate_count')
						.select('id, name, candidate_count')
						.eq('organization_uuid', currentOrganization.id)
						.ilike('name', `%${query}%`)
						.limit(4),
				]);

				const filteredCandidates = (candidatesRes.data || []).filter(
					(c: CandidateResult) => c.email && !recipients.some(r => r.email.toLowerCase() === c.email.toLowerCase())
				);
				const filteredLists = (listsRes.data || []).filter(
					(l: ListResult) => l.candidate_count > 0
				);

				setCandidateResults(filteredCandidates);
				setListResults(filteredLists);
				setShowDropdown(filteredCandidates.length > 0 || filteredLists.length > 0);
			} catch (_error) {
				setCandidateResults([]);
				setListResults([]);
			} finally {
				setIsSearching(false);
			}
		}, 300);

		return () => {
			if (searchTimeoutRef.current)
				clearTimeout(searchTimeoutRef.current);
		};
	}, [inputValue, currentOrganization?.id, recipients]);

	// Close dropdown on outside click
	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
				inputRef.current && !inputRef.current.contains(e.target as Node))
				setShowDropdown(false);
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	const addManualRecipient = () => {
		const trimmed = inputValue.trim();
		if (!trimmed || !trimmed.includes("@") || !onAdd) return;

		if (recipients.some(r => r.email.toLowerCase() === trimmed.toLowerCase())) {
			setInputValue("");
			return;
		}

		onAdd({ email: trimmed, name: "" });
		setInputValue("");
		setShowDropdown(false);
		inputRef.current?.focus();
	};

	const selectCandidate = (candidate: CandidateResult) => {
		if (!onAdd) return;
		onAdd({ email: candidate.email, name: candidate.name, candidateId: candidate.id });
		setInputValue("");
		setCandidateResults([]);
		setListResults([]);
		setShowDropdown(false);
		inputRef.current?.focus();
	};

	const selectList = async (list: ListResult) => {
		if (!onAddMultiple && !onAdd) return;
		setLoadingListId(list.id);
		try {
			const { data } = await supabase
				.from('list_members')
				.select('candidate_id, candidates(id, name, email)')
				.eq('list_id', list.id);

			const newRecipients: EmailRecipient[] = (data || [])
				.filter((m: any) => m.candidates?.email && !recipients.some(r => r.email.toLowerCase() === m.candidates.email.toLowerCase()))
				.map((m: any) => ({
					email: m.candidates.email,
					name: m.candidates.name || "",
					candidateId: m.candidates.id,
				}));

			if (onAddMultiple)
				onAddMultiple(newRecipients);
			else if (onAdd)
				newRecipients.forEach(r => onAdd(r));
		} catch (_error) {
			// silently fail
		} finally {
			setLoadingListId(null);
			setInputValue("");
			setCandidateResults([]);
			setListResults([]);
			setShowDropdown(false);
			inputRef.current?.focus();
		}
	};

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter" || e.key === ",") {
			e.preventDefault();
			if (showDropdown && candidateResults.length > 0)
				selectCandidate(candidateResults[0]);
			else
				addManualRecipient();
		}
		if (e.key === "Escape")
			setShowDropdown(false);
	};

	const recipientInput = onAdd ? (
		<div className="relative">
			<div className="flex gap-1.5">
				<div className="relative flex-1">
					<Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
					<Input
						ref={inputRef}
						value={inputValue}
						onChange={(e) => setInputValue(e.target.value)}
						onKeyDown={handleKeyDown}
						onFocus={() => { if (candidateResults.length > 0 || listResults.length > 0) setShowDropdown(true); }}
						placeholder={t("email.compose.addRecipientPlaceholder", "Search candidates, lists, or type email...")}
						className="text-sm h-8 pl-7"
					/>
				</div>
				<Button
					variant="outline"
					size="sm"
					onClick={addManualRecipient}
					disabled={!inputValue.includes("@")}
					className="h-8 px-2"
					title={t("email.compose.addManualEmail", "Add email")}
				>
					<Plus className="h-3.5 w-3.5" />
				</Button>
			</div>
			{showDropdown && (
				<div
					ref={dropdownRef}
					className="absolute z-50 top-full left-0 right-0 mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-sm shadow-lg max-h-[250px] overflow-y-auto"
				>
					{listResults.length > 0 && (
						<>
							<div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b border-gray-100 dark:border-gray-800">
								{t("email.compose.lists", "Lists")}
							</div>
							{listResults.map((list) => (
								<button
									key={`list-${list.id}`}
									onClick={() => selectList(list)}
									disabled={loadingListId === list.id}
									className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
								>
									<Users className="h-3.5 w-3.5 text-indigo-500 shrink-0" />
									<div className="min-w-0 flex-1">
										<div className="text-sm font-medium truncate">{list.name}</div>
										<div className="text-xs text-gray-500">{list.candidate_count} {t("email.compose.candidates", "candidates")}</div>
									</div>
									{loadingListId === list.id && <span className="text-xs text-muted-foreground animate-pulse">...</span>}
								</button>
							))}
						</>
					)}
					{candidateResults.length > 0 && (
						<>
							{listResults.length > 0 && (
								<div className="px-3 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide border-b border-gray-100 dark:border-gray-800">
									{t("email.compose.candidatesLabel", "Candidates")}
								</div>
							)}
							{candidateResults.map((candidate) => (
								<button
									key={candidate.id}
									onClick={() => selectCandidate(candidate)}
									className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
								>
									<User className="h-3.5 w-3.5 text-gray-400 shrink-0" />
									<div className="min-w-0">
										<div className="text-sm font-medium truncate">{candidate.name}</div>
										<div className="text-xs text-gray-500 truncate">{candidate.email}</div>
									</div>
								</button>
							))}
						</>
					)}
				</div>
			)}
		</div>
	) : null;

	// Show summary for large lists
	if (listName && recipients.length > maxDisplay)
		return (
			<div className="space-y-2">
				<div className="flex items-center gap-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded-sm border border-blue-200 dark:border-blue-800">
					<Users className="h-4 w-4 text-blue-500 dark:text-blue-400" />
					<span className="text-sm font-medium text-blue-700 dark:text-blue-300">
						{t("email.compose.recipientsSummary", "{{count}} candidates from list \"{{listName}}\"", {
							count: recipients.length,
							listName,
						})}
					</span>
				</div>
				{recipientInput}
			</div>
		);

	const displayed = recipients.slice(0, maxDisplay);
	const remaining = recipients.length - maxDisplay;

	return (
		<div className="space-y-2">
			{recipients.length > 0 && (
				<div className="flex flex-wrap gap-1.5">
					{displayed.map((recipient, index) => (
						<Badge
							key={`${recipient.email}-${index}`}
							variant="secondary"
							className="flex items-center gap-1 py-1 px-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
						>
							{recipient.candidateId && <User className="h-3 w-3 text-blue-500" />}
							<span className="text-xs max-w-[200px] truncate">
								{recipient.name || recipient.email}
							</span>
							{onRemove && (
								<button
									onClick={() => onRemove(index)}
									className="ml-0.5 hover:text-red-500 transition-colors"
								>
									<X className="h-3 w-3" />
								</button>
							)}
						</Badge>
					))}
					{remaining > 0 && (
						<Badge variant="outline" className="py-1 px-2 text-xs">
							+{remaining} {t("email.compose.more", "more")}
						</Badge>
					)}
				</div>
			)}
			{recipientInput}
		</div>
	);
};
